import { useState, useRef } from "react";
import { cn } from "../lib/utils";

export interface DragListItem {
  id: string;
  content: React.ReactNode;
  disabled?: boolean;
}

interface DragListProps {
  items: DragListItem[];
  onChange: (items: DragListItem[]) => void;
  disabled?: boolean;
  className?: string;
}

export function DragList({ items, onChange, disabled, className }: DragListProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const dragGhost = useRef<HTMLDivElement | null>(null);

  function handleDragStart(index: number) {
    if (disabled || items[index]?.disabled) return;
    setDragIndex(index);
  }

  function handleDragEnter(index: number) {
    if (dragIndex === null || dragIndex === index) return;
    setOverIndex(index);
  }

  function handleDrop() {
    if (dragIndex === null || overIndex === null || dragIndex === overIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(overIndex, 0, moved);
    onChange(next);
    setDragIndex(null);
    setOverIndex(null);
  }

  function handleDragEnd() {
    setDragIndex(null);
    setOverIndex(null);
  }

  return (
    <div className={cn("drag-list", className)} role="list">
      {items.map((item, index) => (
        <div
          key={item.id}
          role="listitem"
          className={cn(
            "drag-list-item",
            dragIndex === index && "drag-list-item-dragging",
            overIndex === index && dragIndex !== index && "drag-list-item-over",
            (item.disabled || disabled) && "drag-list-item-disabled"
          )}
          draggable={!item.disabled && !disabled}
          onDragStart={() => handleDragStart(index)}
          onDragEnter={() => handleDragEnter(index)}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
        >
          <span
            className="drag-list-handle"
            aria-hidden="true"
            title="Drag to reorder"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <circle cx="4.5" cy="3.5" r="1.2" fill="currentColor" />
              <circle cx="9.5" cy="3.5" r="1.2" fill="currentColor" />
              <circle cx="4.5" cy="7"   r="1.2" fill="currentColor" />
              <circle cx="9.5" cy="7"   r="1.2" fill="currentColor" />
              <circle cx="4.5" cy="10.5" r="1.2" fill="currentColor" />
              <circle cx="9.5" cy="10.5" r="1.2" fill="currentColor" />
            </svg>
          </span>
          <div className="drag-list-content">{item.content}</div>
        </div>
      ))}
      <div ref={dragGhost} style={{ display: "none" }} aria-hidden="true" />
    </div>
  );
}
